import React from 'react';
import { CheckboxMark } from '../../shared/ui/primitives/checkbox';
import { color, control, font, radius, role, shadow, spaceNum } from '../../shared/ui/tokens';

/**
 * The MUI theme's switches, checkboxes and radios, split out of Theme.js so the toggle
 * controls have one owner and read the same as the Radix ones in `shared/ui/primitives`.
 *
 * Redesign iteration 1 (2026-09-29) draws one switch and one checkbox everywhere, whether
 * the control underneath is MUI's, Radix's or the browser's own (css/controls.css has the
 * native box). MUI's defaults are a different shape: a 34 by 14 track under a 20px thumb
 * that overhangs it, and a checkbox drawn as an SVG with a 2px edge and a hole for its tick.
 * So the switch's root, base, track and thumb are all resized here, and the checkbox is given
 * icons of its own. None of it touches `checked`, `onChange` or the input underneath.
 *
 * Ripples are off on all three. The design's hover changes nothing but the cursor, and MUI's
 * ripple doubled as its focus indicator; the outline below replaces it.
 */

/** The focus ring every control draws: a 2px `--brand-focus` outline, 2px clear of its edge. */
const focusRing = {
  outlineStyle: 'solid',
  outlineWidth: 2,
  outlineColor: color.brandFocus,
  outlineOffset: 2,
};

/**
 * FormControlLabel pulls itself 11px left: MUI's allowance for the padding round its own
 * controls, so that a 42px checkbox lines its box up with the text above it. A control
 * drawn at its real size has to hand that back.
 */
const LABEL_PULL = 11;

/** MUI draws its toggle icons in a 24px box: SvgIcon at the medium size. */
const ICON_BOX = 24;

/** A label's distance from the switch or box beside it. */
const LABEL_GAP = spaceNum[10];

const BOX = '.cms-checkbox-box';

/**
 * The icon MUI's checkbox shows in place of its SVGs. It keeps the SVG's 24px footprint with
 * the 18px box where MUI drew its own, so no checkbox moves; the overrides below paint the
 * box. MUI clones an icon with a `fontSize` prop, which is left unread because the box has
 * one size.
 */
function CheckboxIcon({ checked = false, mixed = false }) {
  return (
    <span className="cms-checkbox-icon" aria-hidden="true">
      <span className="cms-checkbox-box">
        {(checked || mixed) && <CheckboxMark mixed={mixed} />}
      </span>
    </span>
  );
}

export const controlOverrides = {
  MuiSwitch: {
    defaultProps: { disableRipple: true },
    /* A 36 by 20 track with a 16px knob inset 2px, as the Radix switch draws it. The track
       is the root's own box now, so the focus ring goes on the root: the root has to keep
       clipping its children, because MUI's input deliberately overhangs the track and
       clipping it is what keeps the click target to the track. The knob keeps a shadow so
       it stays distinct from the track in both states without relying on colour alone. */
    styleOverrides: {
      root: {
        width: control.switchWidth,
        height: control.switchHeight,
        padding: 0,
        borderRadius: control.switchHeight / 2,
        '&:has(.Mui-focusVisible)': focusRing,
        '&:has(.Mui-disabled)': { cursor: 'not-allowed' },
        '.MuiFormControlLabel-labelPlacementEnd > &': {
          marginLeft: LABEL_PULL,
          marginRight: LABEL_GAP,
        },
        // Every switch label is 600, whichever switch it labels.
        '.MuiFormControlLabel-root > & + .MuiFormControlLabel-label': {
          color: color.ink,
          fontWeight: font.weightWanted.semibold,
        },
        '.MuiFormControlLabel-root > & + .MuiFormControlLabel-label.Mui-disabled': {
          color: color.muted,
        },
      },
      switchBase: {
        padding: (control.switchHeight - control.switchKnob) / 2,
        color: color.white,
        '&:hover, &.Mui-checked:hover': { backgroundColor: 'transparent' },
        '&.Mui-checked': {
          color: color.white,
          transform: `translateX(${control.switchWidth - control.switchHeight}px)`,
        },
        '&.Mui-disabled, &.Mui-checked.Mui-disabled': { color: color.white },
        '&.Mui-checked + .MuiSwitch-track': { backgroundColor: color.brand, opacity: 1 },
        // After the checked rule, so a disabled switch is the pale grey whichever way it is set.
        '&.Mui-disabled + .MuiSwitch-track': { backgroundColor: color.neutral300, opacity: 1 },
      },
      thumb: { width: control.switchKnob, height: control.switchKnob, boxShadow: shadow.xs },
      track: {
        borderRadius: control.switchHeight / 2,
        backgroundColor: role.switchOff,
        opacity: 1,
      },
    },
  },

  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: <CheckboxIcon />,
      checkedIcon: <CheckboxIcon checked />,
      indeterminateIcon: <CheckboxIcon mixed />,
    },
    /* The same box as the Radix checkbox: 18px, radius 4, the input edge; --check-on with a
       white tick when checked or a white bar when mixed; --fill in a --border-light edge when
       disabled, solid neutral when disabled and checked. MUI's own colour, which reached
       the old SVG through currentColor, no longer paints anything. */
    styleOverrides: {
      root: {
        '& .cms-checkbox-icon': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: ICON_BOX,
          height: ICON_BOX,
        },
        [`& ${BOX}`]: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          width: control.checkSize,
          height: control.checkSize,
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: role.inputBorder,
          borderRadius: radius.check,
          backgroundColor: color.white,
          color: color.white,
          transition: 'background-color 150ms, border-color 150ms',
        },
        [`&:hover ${BOX}`]: { borderColor: color.muted },
        [`&.Mui-checked ${BOX}, &.MuiCheckbox-indeterminate ${BOX}`]: {
          borderColor: role.checkOn,
          backgroundColor: role.checkOn,
        },
        [`&.Mui-disabled ${BOX}`]: { borderColor: color.borderLight, backgroundColor: color.fill },
        [`&.Mui-checked.Mui-disabled ${BOX}, &.MuiCheckbox-indeterminate.Mui-disabled ${BOX}`]: {
          borderColor: color.neutral500,
          backgroundColor: color.neutral500,
        },
        [`&.Mui-focusVisible ${BOX}`]: focusRing,
        // The box sits 3px inside its icon, so this leaves the label 10px from the box.
        '.MuiFormControlLabel-labelPlacementEnd > &': {
          paddingRight: LABEL_GAP - (ICON_BOX - control.checkSize) / 2,
        },
        '.MuiFormControlLabel-root > & + .MuiFormControlLabel-label': { color: color.ink },
        '.MuiFormControlLabel-root > & + .MuiFormControlLabel-label.Mui-disabled': {
          color: color.muted,
        },
      },
    },
  },

  MuiRadio: {
    defaultProps: { disableRipple: true },
    /* Radios follow the checkbox: the input edge at rest, --check-on when chosen, the same
       disabled pair, and the same ring, drawn round the icon's 24px box so it clears the
       20px circle by 2px. MUI's own icon is kept; only its colour and the ring change. */
    styleOverrides: {
      root: {
        color: role.inputBorder,
        '&.Mui-checked': { color: role.checkOn },
        '&.Mui-disabled': { color: color.borderLight },
        '&.Mui-checked.Mui-disabled': { color: color.neutral500 },
        '&.Mui-focusVisible > span': { ...focusRing, outlineOffset: 0, borderRadius: '50%' },
        // The circle sits 2px inside its icon, so this leaves the label 10px from it.
        '.MuiFormControlLabel-labelPlacementEnd > &': {
          paddingRight: LABEL_GAP - 2,
        },
        '.MuiFormControlLabel-root > & + .MuiFormControlLabel-label.Mui-disabled': {
          color: color.muted,
        },
      },
    },
  },
};

export default controlOverrides;
